import { FormattedMessage, useIntl } from 'react-intl';
import React, { SyntheticEvent, useRef, useState } from 'react';
import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { LanguageCode, MarketCode } from '@tink/config/src/markets';
import { ProductType } from '@tink/core/src/types';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { getLanguageCode } from '../../../utilities/utils';
import { UnderlinedTextButton } from '../../../components/atoms/Button/TextButton';
import { DocumentOverlayLink } from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { privacyPolicyHTMLFraudPrevention } from '../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicy';
import { PageView } from '../../../models/analytics';
import { termsAndConditionFraudPrevention } from '../../../core/screens/Legal/TermsAndConditions/TermsAndConditions';
import { ScopesDetailOverlay } from '../../../components/screens/molecules/LegalDisclaimer/LegalDisclaimerAuth/molecules/ScopesDetailOverlay/ScopesDetailOverlay';
import { ScopeItem } from '../../../lib-api/services/Oauth';
import { messages } from '../../messages';
import { FooterLegalDisclaimer, FooterText } from '../../../components/atoms/Footer/Footer';
import {
  ClientLegalDisclaimer as ClientLegalDisclaimerImport,
  ClientLegalDisclaimerProps,
  showClientLegalTexts,
} from '../../../core/components/ClientLegalDisclaimer/ClientLegalDisclaimer';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { ProductDimension } from '../../../models/product';
import { useProduct } from '../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { isFraudPreventionEnabled as shouldUseFraudPreventionDocuments } from '../../../payments/helpers/fraud-prevention';
import { getTermsAndConditionsMarketUrl } from '../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';
import {
  AppConsentClientLegalComponentProps,
  AppConsentClientLegal as AppConsentClientLegalImport,
} from './AppConsentClientLegal';

export type ConsentLegalFooterProps = {
  clientId: string;
  clientName: string;
  scopesDescriptionsList: ScopeItem[];
  featureFlags: ConfigurationFeatureFlag[];
  ClientLegalDisclaimer?: React.ComponentType<ClientLegalDisclaimerProps>;
  AppConsentClientLegal?: React.ComponentType<AppConsentClientLegalComponentProps>;
};

export type LegalDocumentArgs = {
  languageCode: LanguageCode;
  marketCode: MarketCode;
  productType: ProductType;
  productDimension: ProductDimension;
  config: ConfigurationModel;
};

export const getTermsAndConditionsHTML = ({
  languageCode,
  marketCode,
  productType,
  productDimension,
  config,
}: LegalDocumentArgs) => {
  const isFraudPreventionEnabled = shouldUseFraudPreventionDocuments(
    productType,
    productDimension,
    config
  );

  if (isFraudPreventionEnabled) {
    return languageCode === LanguageCode.de
      ? termsAndConditionFraudPrevention[LanguageCode.de]
      : termsAndConditionFraudPrevention[LanguageCode.en];
  }

  return getTermsAndConditionsMarketUrl({ language: languageCode, market: marketCode });
};

export const getPrivacyPolicyHTML = ({
  languageCode,
  marketCode,
  productType,
  productDimension,
  config,
}: LegalDocumentArgs) => {
  const isFraudPreventionEnabled = shouldUseFraudPreventionDocuments(
    productType,
    productDimension,
    config
  );

  if (isFraudPreventionEnabled) {
    return languageCode === LanguageCode.de
      ? privacyPolicyHTMLFraudPrevention[LanguageCode.de]
      : privacyPolicyHTMLFraudPrevention[LanguageCode.en];
  }

  return getPrivacyPolicyMarketUrl({ market: marketCode, language: languageCode });
};

export const ConsentLegalFooter = ({
  clientId,
  clientName,
  featureFlags,
  scopesDescriptionsList,
  ClientLegalDisclaimer = ClientLegalDisclaimerImport,
  AppConsentClientLegal = AppConsentClientLegalImport,
}: ConsentLegalFooterProps) => {
  const { formatMessage } = useIntl();
  const configuration = useConfiguration();
  const { productType, productDimension } = useProduct();
  const { locale, market } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const [isScopesDetailOverlayOpen, setScopesOverlayOpen] = useState(false);
  const scopesButtonRef = useRef<HTMLAnchorElement>(null);

  const openScopesModal = (event: SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setScopesOverlayOpen(true);
  };

  const closeScopesModal = () => {
    setScopesOverlayOpen(false);

    if (scopesButtonRef.current) {
      scopesButtonRef.current.focus();
    }
  };

  const hideScopes = featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_SCOPES_HIDDEN);
  const hideLegalText = featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_TERMS_HIDDEN);
  const shouldShowAppSpecificLegalDocs = featureFlags.includes(
    ConfigurationFeatureFlag.SHOW_APP_SPECIFIC_LEGAL_DOCS
  );

  const shouldShowClientLegalDocs = showClientLegalTexts({
    clientId,
    configuration: configuration.data,
  });

  const termsAndConditionsHTMLUrl = getTermsAndConditionsHTML({
    languageCode,
    marketCode: market,
    productType,
    productDimension,
    config: configuration.data,
  });

  const privacyPolicyHTMLUrl = getPrivacyPolicyHTML({
    languageCode,
    marketCode: market,
    productType,
    productDimension,
    config: configuration.data,
  });

  return (
    <FooterLegalDisclaimer stickToBottom alignItems="center">
      <FooterText>
        {shouldShowClientLegalDocs ? (
          <ClientLegalDisclaimer clientId={clientId} clientName={clientName} />
        ) : (
          <div data-testid="legal-disclaimer-consent">
            {!hideLegalText && (
              <>
                {shouldShowAppSpecificLegalDocs && (
                  <AppConsentClientLegal
                    clientId={clientId}
                    clientName={clientName}
                    locale={locale}
                    market={market}
                  />
                )}
                {!shouldShowAppSpecificLegalDocs && (
                  <FormattedMessage
                    {...messages.paymentConsentTermsText}
                    values={{
                      privacyPolicy: (
                        <DocumentOverlayLink
                          documentUrl={privacyPolicyHTMLUrl}
                          pageView={PageView.PRIVACY_POLICY_SCREEN}
                          overlayAriaLabel={formatMessage(messages.privacyPolicy)}
                        >
                          {formatMessage(messages.privacyPolicy)}
                        </DocumentOverlayLink>
                      ),
                      termsAndConditions: (
                        <DocumentOverlayLink
                          documentUrl={termsAndConditionsHTMLUrl}
                          pageView={PageView.TERMS_AND_CONDITION_SCREEN}
                          overlayAriaLabel={formatMessage(messages.termsAndConditions)}
                        >
                          {formatMessage(messages.termsAndConditions)}
                        </DocumentOverlayLink>
                      ),
                    }}
                  />
                )}
              </>
            )}{' '}
            {!hideScopes && (
              <>
                <UnderlinedTextButton
                  onClick={openScopesModal}
                  as="a"
                  role="button"
                  tabIndex={0}
                  href=""
                  ref={scopesButtonRef}
                >
                  <FormattedMessage {...messages.readMore} />
                </UnderlinedTextButton>
                {isScopesDetailOverlayOpen && (
                  <ScopesDetailOverlay close={closeScopesModal} scopes={scopesDescriptionsList} />
                )}
              </>
            )}
          </div>
        )}
      </FooterText>
    </FooterLegalDisclaimer>
  );
};
